import {ExclamationCircleOutlined} from '@ant-design/icons';
import ProTable from '@ant-design/pro-table';
import {Divider, Modal, notification, Typography} from 'antd';
import MenuTemplateCart from 'components/MenuTemplateCart';
import localization from 'localization';
import {isEmpty} from 'lodash';
import React, {useRef, useState} from 'react';
import {useNavigate} from 'react-router-dom';
import {deleteOrderTemplate, getOrderTemplates} from 'utils/request/internalOrdering';
import {dateFormat, handleErrorFetch, moneyFormat, sortDirectionConverter} from 'utils/utils';

const OrderTemplateTable = () => {
	const locale = localization.InternalOrdering.MenuCreator.Table;
	const tableRef = useRef(null);
	const menuTemplateCartRef = useRef(null);
	const navigate = useNavigate();

	const [loadingDelete, setLoadingDelete] = useState(false);
	const [cart, setCart] = useState([]);
	const [previewData, setPreviewData] = useState(0);

	const onClickDetail = record => {
		const itemsData = record?.items || [];
		const tempCart = itemsData.map(item => {
			return {
				dataOrigin: {},
				menuItem: item,
				mappingHash: item.mappingHash,
			};
		});
		setCart(tempCart);
		setPreviewData(record);
		menuTemplateCartRef?.current?.openDrawer?.();
	};

	const deleteCofirmation = (id, label) => {
		const actionLocale = localization.InternalOrdering.MenuCreator.DeleteConfirmation;
		Modal.confirm({
			title: actionLocale.title,
			icon: <ExclamationCircleOutlined />,
			content: actionLocale.message.replace('{{label}}', label),
			centered: true,
			cancelText: actionLocale.cancelText,
			okText: actionLocale.okText,
			okButtonProps: {loading: loadingDelete, danger: true},
			cancelButtonProps: {loading: loadingDelete},
			onOk: async () => {
				try {
					setLoadingDelete(true);
					const response = await deleteOrderTemplate({id});
					if (response.success) {
						tableRef?.current?.reload();
						notification.success({
							message: actionLocale.Success.message,
							description: actionLocale.Success.description,
						});
					}
				} catch (error) {
					handleErrorFetch({
						message: actionLocale.Failed.message,
						description: actionLocale.Failed.description,

					});
				} finally {
					setLoadingDelete(false);
				}
			},
		  });
	};

	const columns = [
		{
			title: locale.type,
			width: 131,
			key: 'type',
			dataIndex: 'type',
			search: false,
			filterMultiple: false,
			filters: [
				{
					text: 'KOL',
					value: 'KOL',
				},
				{
					text: 'Bulk',
					value: 'bulk',
				},
				{
					text: 'Replacement',
					value: 'replacement',
				},
			],
		},
		{
			title: locale.id,
			width: 200,
			key: 'id',
			dataIndex: 'id',
			render: id => (
				<Typography.Text
					copyable
				>{id?.toUpperCase?.()}</Typography.Text>
			),
		},
		{
			title: locale.label,
			width: 222,
			key: 'name',
			dataIndex: 'name',
		},
		{
			title: locale.qty,
			width: 78,
			key: 'qty',
			search: false,
			render: (_, record) => {
				let qtyCount = 0;
				record.items.forEach(item => qtyCount+= item.quantity);
				return qtyCount;
			},
		},
		{
			title: locale.price,
			width: 175,
			key: 'price',
			search: false,
			sorter: true,
			dataIndex: 'subtotal',
			render: subtotal => moneyFormat({value: subtotal}),
		},
		{
			title: locale.createdBy,
			width: 155,
			key: 'createdBy',
			dataIndex: ['creator', 'username'],
		},
		{
			title: locale.createdAt,
			width: 195,
			key: 'createdAt',
			sorter: true,
			defaultSortOrder: 'descend',
			dataIndex: 'createdAt',
			search: false,
			render: createdAt => dateFormat(createdAt),
		},
		{
			title: locale.updatedBy,
			width: 155,
			key: 'updatedBy',
			search: false,
			dataIndex: 'updatedBy',
		},
		{
			title: locale.updatedAt,
			width: 195,
			key: 'updatedAt',
			sorter: true,
			dataIndex: 'updatedAt',
			search: false,
			render: updatedAt => updatedAt ? dateFormat(updatedAt) : '-',
		},
		{
			title: locale.action,
			width: 197,
			key: 'action',
			search: false,
			fixed: 'right',
			render: (_, record) => (
				<div className='flex gap-2 justify-center items-center'>
					<a
						className='text-antd-blue-6'
						onClick={() => navigate(`/internal-ordering/menu-creator/edit/${record.id}`, {state: {
							requestType: record.type,
						}})}
					>{locale.edit}</a>
					<Divider
						className='m-0'
						type='vertical' />
					<a
						className='text-antd-blue-6'
						onClick={() => onClickDetail(record)}
					>{locale.detail}</a>
					<Divider
						className='m-0'
						type='vertical' />
					<a
						onClick={() => deleteCofirmation(record.id, record.name)}
						className='text-antd-red-6'
					>{locale.delete}</a>
				</div>
			),
		},
	];

	const fetchTableData = async (params, sorter, filters) => {
		try {
			/**
			 * Params consist of pagers and table query
			 * Sorter consist of column sort
			 * Filters consist of column filter
			 */

			const sortOrderKey = isEmpty(sorter) ? 'descend' : Object.values?.(sorter)?.[0];
			const sortByKey = isEmpty(sorter) ? 'createdAt' : Object.keys?.(sorter)?.[0];

			const payload = {
				limit: params?.pageSize,
				page: params?.current - 1,
				sortBy: sortByKey,
				sortOrder: sortDirectionConverter(sortOrderKey),
				search: {
					id: params?.id?.toLowerCase?.() || null,
					name: params?.name || null,
					createdBy: params?.createdBy || null,
					type: filters?.type?.[0] || null,
				},
			};
			const response = await getOrderTemplates(payload);
			return {
				data: response.data.rows,
				success: response.success,
				total: response.data.count,
			};
		} catch (error) {
			handleErrorFetch(error);
		}
	};

	return (
		<div>
			<MenuTemplateCart
				ref={menuTemplateCartRef}
				viewOnly
				title={`${previewData.id?.toUpperCase?.()} ∙ ${previewData.name}`}
				cart={cart}
				customFooter={() => (
					<div className='flex flex-col gap-2'>
						<div className='flex justify-between'>
							<div>{locale.subtotal}</div>
							<div>{moneyFormat({value: previewData.subtotal || 0})}</div>
						</div>
						<Divider
							className='m-0'
							dashed />
						<div className='flex justify-between'>
							<div>{locale.total}</div>
							<Typography.Text strong>{moneyFormat({value: previewData.subtotal || 0})}</Typography.Text>
						</div>
					</div>
				)}
			/>
			<ProTable
				actionRef={tableRef}
				className="mt-4 ResetSearchTableMargin px-6"
				rowKey='id'
				columns={columns}
				request={fetchTableData}
				toolBarRender={false}
				scroll={{x: 'max-content'}}
				onReset={() => tableRef?.current?.reset()}
				search={{
					layout: 'vertical',
				}}
				pagination={{
					defaultPageSize: 25,
					showSizeChanger: true,
					showQuickJumper: true,
					pageSizeOptions: ['10', '25', '50', '75', '100'],
				}}
			/>
		</div>
	);
};

export default OrderTemplateTable;